import {ChangeDetectionStrategy, Component, inject, input, OnInit, output} from '@angular/core';
import {
  AdvertisementStatisticsFormField
} from "../../../advertisement-statistics-modal/types/advertisement-statistics-modal.types";
import {newProductCardFormFields} from "../../configs/product-card-configs";
import {
  ProductCardsRequestQueryParamsStateService
} from "../../services/product-cards-request-query-params-state.service";
import {
  ProductCard,
  ProductCardDialogData
} from "../../types/product-card.types";
import {ProductCardModalService} from "../../services/product-card-modal.service";
import {NewProductCardService} from "../../services/new-product-card.service";
import {ProductCardMarketplaceStateService} from "../../services/product-card-marketplace-state.service";
import {CloseModalReason} from "../../../../../types/modal.types";
import {ErrorService} from "../../../../../services/error.service";
import {BaseModalService} from "../../../services/base-modal.service";
import {MatButton} from "@angular/material/button";
import {MatTab, MatTabGroup, MatTabChangeEvent} from "@angular/material/tabs";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatInput} from "@angular/material/input";
import {MatListOption, MatSelectionList} from "@angular/material/list";
import { MatLabel } from "@angular/material/form-field";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-new-product-card',
  standalone: true,
  imports: [
    MatTabGroup,
    MatTab,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSelectionList,
    MatListOption,
    MatButton,
    LoaderComponent
  ],
  templateUrl: './new-product-card.component.html',
  styleUrls: [
    '../../../base-modal/base-modal.component.scss',
    './new-product-card.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewProductCardComponent implements OnInit {
  productCardDialogData = input.required<ProductCardDialogData>();
  isLoading = input.required<boolean>();
  updateProductCardInTable = output<number | null>();
  handleCloseDialog = output<CloseModalReason>();
  handleLoadingState = output<boolean>();

  private productCardsRequestQueryParamsStateService: ProductCardsRequestQueryParamsStateService = inject(ProductCardsRequestQueryParamsStateService);
  private productCardModalService: ProductCardModalService = inject(ProductCardModalService);
  private newProductCardService: NewProductCardService = inject(NewProductCardService);
  private productCardMarketplaceStateService: ProductCardMarketplaceStateService = inject(ProductCardMarketplaceStateService);
  private errorService: ErrorService = inject(ErrorService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public readonly newProductCardFormFields = newProductCardFormFields;
  public productCardFormGroup = new FormGroup({});
  public selectedTabIndex: number = 0;
  public searchValue: string = '';
  public productCards: Array<ProductCard> = [];
  public hasProductCards: boolean = false;
  public selectedProductCards: Array<ProductCard> = [];

  public async ngOnInit(): Promise<void> {
    this.initForm();
    await this.fetchProductCardsData();
  }

  public handleTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
  }

  public getCurrentProductTabLabel(): string {
    const count = this.productCards.length || '-';
    return `Выбрать из существующих (${count})`;
  }

  public async handleAttach(): Promise<void> {
    if (!this.productCardMarketplaceStateService.productCardMarketplaceState()) {
      this.errorService.showErrorMessage('Ошибка!');
      return;
    }

    this.handleLoadingState.emit(true);
    const articleId = this.productCardFormGroup.get('articleId' as string)?.value;
    this.selectedTabIndex ?
      await this.attachProductCard(articleId) :
      await this.createProductCard(this.productCardFormGroup);
  }

  public getDisabledBtn(): boolean {
    return this.selectedTabIndex ?
      !this.selectedProductCards.at(0)?.id :
      !this.productCardFormGroup.get('articleId' as string)?.value;
  }

  public getBtnLabel(): string {
    return this.selectedTabIndex ? 'Прикрепить' : 'Создать';
  }

  private initForm(): void {
    const fieldNames: Array<string> = newProductCardFormFields.map((field: AdvertisementStatisticsFormField) => field.fieldName);
    this.productCardFormGroup = this.baseModalService.initForm(fieldNames);
    const newProductCard = this.newProductCardService.setNewProductCard(this.productCardDialogData().parentData);
    this.productCardFormGroup.patchValue(newProductCard);
  }

  public async fetchProductCardsData(): Promise<void> {
    this.handleLoadingState.emit(true);
    this.productCardsRequestQueryParamsStateService.updateProductCardsRequestQueryParams(this.searchValue);
    this.productCards = await this.productCardModalService.getProductCards().catch(() => []);
    this.hasProductCards = !!this.productCards.length;
    this.handleLoadingState.emit(false);
  }

  private async createProductCard(productCardFormGroup: FormGroup): Promise<void> {
    const createProductCard = this.newProductCardService.setCreateProductCard(productCardFormGroup);
    await this.productCardModalService.createProductCard(createProductCard)
      .then((productCard) => this.updateProductCardInTable.emit(productCard.id))
      .finally(() => this.handleLoadingState.emit(false));
  }

  private async attachProductCard(articleId: number): Promise<void> {
    const id = this.selectedProductCards.at(0)?.id || 0;
    const attachProductCard = this.newProductCardService.setAttachProductCard(id, articleId);
    await this.productCardModalService.attachProductCard(attachProductCard)
      .then(() => this.updateProductCardInTable.emit(id))
      .finally(() => this.handleLoadingState.emit(false));
  }
}
